import type { DownloadPlan } from '@mimir/client-common/fileSync/downloadIdentity';
import { makeTitle, refineArtifactReferenceForResume } from '@mimir/client-common/fileSync/downloadPlanning';
import type { SyncItemInfo } from '@mimir/client-common/fileSync/premiereTypes';
import type { PostDownloadHooks } from '@mimir/client-common/model/ongoingDownload';
import { type PercentageHandler, type OngoingRestoreStatus, type DownloadReporter, type ConstructedOngoingDownload, getId, type OngoingDownload, type BuiltinOngoingDownload, isBuiltinOngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import type { NleProjectBin } from '@mimir/primitive/nle/bin';
import { getSubclipToImport } from '@mimir/client-common/premiere/subclip';
import { RestoreTier } from '@mimir/common/dto/restoreMediaRequestDto';
import { TransferStateDto } from '@mimir/common/dto/transferRequestDtos';
import { isInstanceOfVirtualClip } from '@mimir/common/dto/virtualClipUtil';
import { isCurrentlyLive, isMediaOnline } from '@mimir/common/item/media';
import { PREMIERE_STANDALONE_STORAGE_ID } from '@mimir/common/util/constants';
import { createUuid } from '@mimir/common/util/createUuid';
import type { Fraction } from '@mimir/common/util/fractionType';
import { addFractions, approximateFractionValue, multiplyFractionBy } from '@mimir/common/util/integerMath';
import { invariant } from '@mimir/common/util/invariant';
import type { TimeRange } from '@mimir/common/util/timeRange';
import Axios, { type CancelToken } from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import type { StorageProfile } from '../desktop/storageSettings';
import { showToast, TOAST } from '@mimir/web/eventBus';
import { addOrUpdateBatchImport } from '../globals/batchImports';
import { cancelOngoingDownload } from '../globals/cancelOngoingDownload';
import { getGlobalStore } from '../store';
import { getItem, getItemRelations } from '../util/api';
import { showTransferCompleteToast } from '../util/showTransferCompleteToast';
import { sendItemToStorage } from '../util/storageManager';
import { finishOngoingDownload } from '../nle/download/download';
import { getMarkers } from './getMarkers';
import { type StrictSyncLocalMediaStateParams, getSyncedLocalMediaState, syncLocalMediaState, refreshPremiereClipInfos } from './syncing';
import type { LoadedItem, ClientItem } from '@mimir/common/dto/searchResultHit';
import { makeDownloadProgressReporter } from '../globals/downloadProgress';
import { importIntoNle } from './importIntoNle';
import type { BatchImportNotifier } from '@mimir/client-common/model/batchImportNotifier';
import { isInAdobeApp, isInElectron, isInPremiere, type AdobePanelContext, type ElectronContext } from '@mimir/common/context/applicationContext';
import type { PremiereDropFrameTimecodeDisplayFormat } from '@mimir/primitive/nle/timecode';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import { exceptionToString } from '@mimir/common/util/exception';
import { DEFAULT_DATE_PROVIDER } from '@mimir/common/util/dateProvider';
import type { Logger } from '@mimir/common/types/logger';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { getBuiltinDownloadDestinationPath, getDownloadDestination, getPossibleDestinationPaths, tryGetLocallyAvailableFile } from '@mimir/client-common/fileSync/downloadDestinationUtils';
import { isDrpFile, isPremiereProjectFile } from '../nle/ui/misc';
import type { ColdStorageRestoreOps } from '@mimir/client-common/coldStorage/coldStorageRestoreOps';
import { getMediaFrameRateFromItem } from '@mimir/common/util/playableItemUtils';
import { sleep } from '@mimir/common/async/timing';
import { neverCancel } from '@mimir/common/async/async';
import { appendFileAsync, deleteFileAsync, getFileExtension, isFileAvailableLocally, readFileAsync } from '@mimir/common/util/fs';
import { getExternalSmTransferMonitor } from '../globals/externalSmTransferMonitorPolling';
import { clipInfosArray } from '../globals/premiere';
import { logErrorAndShowToast } from '../util/uiError';
import { downloadItemArtifact } from '@mimir/client-common/download/downloadItemArtifact';
import { DefaultBackendOps } from '@mimir/client-common/util/defaultBackendOperations';
import type { GrowingFileSession } from '@mimir/common/types/growingFileSession';
import { MIMIR_ARTIFACT_KEY } from '@mimir/primitive/nle/metadata';
import { linkItemToDestinations } from '../desktop/utils';
import { RelationType } from '@mimir/common/dto/relationsEntity';
import type { TimelineInsertion } from '@mimir/primitive/nle/sequence';

/**
  importClip contains logic for quite a few things.
  - single clip
  - subclip
  - virtual clip
  - downloading with built-in downloader or external storage manager
  - downloading of segments for live-ingested media
  - syncing from the sync page
  - etc.

  virtualClipInfo is needed when importing, not for syncing an already imported clip.
  When imported, virtual clips record the original item's ID in the project and that
  is used for syncing, since virtual clips do not have own media.
 */
export type ImportableItem = SyncItemInfo & Pick<ClientItem, 'virtualClipInfo'>;

export async function importClip(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    notifier: BatchImportNotifier | undefined,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    coldStorageRestoreOps: ColdStorageRestoreOps,
  },
  config: {
    downloadPath: string,
    isStandaloneMode: boolean,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
    shouldUsePremierePreferences: boolean,
    isProxyMode: boolean,
    skippedFileExtensions: string[],
  },
  params: {
    batchImportId?: string,
    doesMasterClipExistInBin ?: boolean,
    onProgress?: PercentageHandler,
    isSubclip?: boolean,
    insertIntoSequence?: boolean,
    ongoingRestoreStatus?: OngoingRestoreStatus,
    originItem: ImportableItem,
    shouldRefreshExistingClip?: boolean,
    storageParams: Pick<StorageProfile, 'storageId'>,
    targetBin?: NleProjectBin,
    timeRange?: TimeRange | null,
    timelineInsertion: TimelineInsertion | null,
    title?: string,
    eagerlyImportOtherRes?: boolean,
    cancelToken?: CancelToken,
    plan: DownloadPlan,
    isProjectTemplate?: boolean,
    sequenceOrder?: number,
    assignedTitle?: string,
    startTimeSeconds: Fraction | null,
    timeDisplayFormat: PremiereDropFrameTimecodeDisplayFormat | null,
  },
): Promise<void> {
  const {
    batchImportId,
    doesMasterClipExistInBin = false,
    isSubclip = false,
    insertIntoSequence = false,
    ongoingRestoreStatus,
    originItem,
    shouldRefreshExistingClip = false,
    storageParams: { storageId },
    targetBin,
    timeRange = null,
    timelineInsertion,
    title = '',
    isProjectTemplate = false,
    sequenceOrder,
    assignedTitle,
    startTimeSeconds,
  } = params;
  const artefact = params.plan.dlId.artefact;

  const virtualClip = isInstanceOfVirtualClip(originItem.virtualClipInfo) ? originItem.virtualClipInfo : null;

  const plan = params.plan;
  const subclip = getSubclipToImport(services.logger, plan, isSubclip, timeRange, virtualClip, title, originItem);

  const cancelTokenSource = Axios.CancelToken.source();
  if (params.cancelToken) {
    void params.cancelToken.promise.then((what) => cancelTokenSource.cancel(what.message));
  }
  const reporter: DownloadReporter = {
    onSuccess() {
      services.notifier?.incrementNumOfFilesDownloaded();
    },
    onReject(cause: unknown) {
      services.notifier?.markFailed(cause);
    },
    onProgress: params.onProgress ?? (() => {}),
    onActivity(activity: string) {
      services.notifier?.setDescription(activity);
    }
  };

  const syncParams: StrictSyncLocalMediaStateParams = {
    item: originItem,
    subclip,
    downloadIdentity: plan.dlId
  };

  invariant(originItem, 'Expected item');
  invariant(originItem.id, 'Expected item id');
  const localMediaState = await getSyncedLocalMediaState(services, syncParams);
  invariant(localMediaState != null, 'Expected non-null from syncLocalMediaState');

  const { applicationContext } = services;

  // If this is a mogrt item, construct a map of filename to item ID
  // for all its mogrt children.
  const mogrtChildrenMap: Record<string, string> = {};
  const itemFileExtension = originItem.originalFileName ? getFileExtension(services.applicationContext.nodeApi, originItem.originalFileName) : '';
  if (itemFileExtension.toLowerCase() === '.mogrt') {
    const mogrtChildrenRelations = await getItemRelations(originItem.id, {
      relationType: RelationType.MogrtChildren,
    });

    for (const relation of mogrtChildrenRelations) {
      if (relation.item.originalFileName) {
        // Due to bugs like #8151, #9671, the originalFileName may have item ID suffixed to it when the file
        // was uploaded to non-Mimir bucket. We need to remove it to get the original filename that matches
        // the one from the project clip.
        const originalFileNameWithSuffixRemoved = relation.item.originalFileName.replace(`_${relation.item.id}`, '');
        mogrtChildrenMap[originalFileNameWithSuffixRemoved] = relation.item.id;
      }
    }
  }

  const isPremiereProject = isPremiereProjectFile(localMediaState.mediaDestination.legacyDestinationPath) || isDrpFile(localMediaState.mediaDestination.legacyDestinationPath);
  let sourceItem: ImportableItem | LoadedItem = originItem;

  // Resolve source item: virtual clip → master clip; live → re-fetch for latest segment count.
  if (virtualClip != null) {
    sourceItem = await getItem(virtualClip.masterClipId);
  } else if (!virtualClip && isCurrentlyLive(originItem)) {
    sourceItem = await getItem(originItem.id);
  }

  const postDownloadHooks: PostDownloadHooks = [];
  if (isInElectron(applicationContext) && !applicationContext.dvrMode) {
    postDownloadHooks.push(async (filePath: string) => {
      await linkItemToDestinations(
        {
          ...services,
          applicationContext,
        },
        config,
        filePath,
        sourceItem,
        plan.dlId,
      );
    });
  }

  let markerClippingStartTime: number | undefined;
  let markerClippingEndTime: number | undefined;
  let premiereStartTimeSeconds = startTimeSeconds;
  const artRefType = plan.dlId.artifactReference.type;
  if ((artRefType === 'TsSegmentRange' || artRefType === 'AudioTsSegmentRange') && sourceItem.media?.segmentDuration) {
    const artRef = plan.dlId.artifactReference;
    invariant(artRef.type === 'TsSegmentRange' || artRef.type === 'AudioTsSegmentRange', 'Expected segment range artifact reference');
    const segmentDuration = approximateFractionValue(sourceItem.media.segmentDuration) * 1000;
    markerClippingStartTime = artRef.segmentTimes.first * segmentDuration;
    markerClippingEndTime = artRef.segmentTimes.last * segmentDuration + segmentDuration;

    // See commit fa805a9 to understand why we do not do this for Premiere also.
    if (isInElectron(applicationContext) && applicationContext.dvrMode) {
      const firstSegmentStartOffset = multiplyFractionBy(sourceItem.media.segmentDuration, artRef.segmentTimes.first);
      premiereStartTimeSeconds = startTimeSeconds ? addFractions(startTimeSeconds, firstSegmentStartOffset) : firstSegmentStartOffset;
    }
  }
  const markers = await getMarkers(sourceItem.id, markerClippingStartTime, markerClippingEndTime);

  const highresIsOffline = artefact === 'highres' ? !isMediaOnline(sourceItem) : false;
  const restoreStatus = highresIsOffline ? ongoingRestoreStatus : undefined;

  const ongoingDownload: ConstructedOngoingDownload = {
    storageId,
    artefact,
    subclip,
    insertIntoSequence,
    downloadDestination: getDownloadDestination(isPremiereProject, localMediaState.mediaDestination, config.isStandaloneMode, config.downloadToSubfolderPaths),
    id: createUuid(),
    itemId: sourceItem.id,
    mediaFrameRate: getMediaFrameRateFromItem(sourceItem) || undefined,
    transferState: TransferStateDto.PROGRESSING,
    title: makeTitle(plan, sourceItem, assignedTitle),
    yetToBeSentToServer: true,
    batchImportId,
    targetBin,
    doesMasterClipExistInBin,
    originalFileName: sourceItem.originalFileName,
    reporters: [reporter, makeDownloadProgressReporter(plan.dlId)],
    artifactReference: refineArtifactReferenceForResume(plan.dlId.artifactReference, localMediaState.segmentedMediaState),
    cancelTokenSource,
    dlId: plan.dlId,
    deliver: [plan.deliver],
    isProjectTemplate,
    sequenceOrder,
    premiereStartTimeSeconds,
    premiereTimeDisplayFormat: params.timeDisplayFormat,
    postDownloadHooks,
    mogrtChildrenMap,
    timelineInsertion,
    markers,
    importAfterCompletion: !shouldRefreshExistingClip,
    highresIsOffline,
    restoreStatus,
  };

  try {
    const odId = getId(ongoingDownload);
    const isBatchImport = !isEmpty(batchImportId);
    const existingOngoingDownload: OngoingDownload | null = services.ongoingDownloadOps.getOngoingDownload(odId);
    if (isBatchImport) {
      addOrUpdateBatchImport(ongoingDownload, services.notifier);
      const importableLocalPath = await tryGetLocallyAvailableFile(services, ongoingDownload, localMediaState.isAvailableLocally ? localMediaState.localPath : undefined);
      if (importableLocalPath) {
        ongoingDownload.reporters?.forEach((r) => r.onSuccess());
        await importIntoNle(services, config, ongoingDownload, importableLocalPath);
        return;
      }

      // File is not locally available — check if it's in cold storage before starting a download.
      if (artefact === 'highres' && !isMediaOnline(sourceItem)) {
        if (config.isStandaloneMode) {
          const store = getGlobalStore();
          const tier = store.state.config.tenantConfig.archiveConfig?.defaultRestoreTier || RestoreTier.Expedited;
          const retentionDays = store.state.config.tenantConfig.archiveConfig?.defaultRetentionDays || 5;

          await services.coldStorageRestoreOps.requestRestore(sourceItem.id, tier, retentionDays);
          return;
        } else {
          invariant(storageId, 'Expected storage id to be defined when importing through storage manager!');
          const coldTransferResponse = await sendItemToStorage(
            storageId,
            sourceItem.id,
            artefact,
            ongoingDownload.artifactReference,
          );
          if (coldTransferResponse?.id) {
            ongoingDownload.id = coldTransferResponse.id;
            ongoingDownload.yetToBeSentToServer = false;
            ongoingDownload.originalFileName = coldTransferResponse.originalFileName;
            services.ongoingDownloadOps.addOrUpdateOngoingDownload(ongoingDownload);
            await completeExternalSmDownload(services, config, ongoingDownload, odId, storageId);
          }
          return;
        }
      }

      if (existingOngoingDownload) {
        if ((virtualClip || subclip !== null) && existingOngoingDownload.batchImportId === batchImportId) {
          // This is a virtual clip of the source clip that's being downloaded.
          // Add it to the batch import.
          addOrUpdateBatchImport(ongoingDownload, services.notifier);
        }
        // there is already an ongoing download from a separate single/batch operation.
        // do not add it to the current one.
        // TODO: should probably check what happens if that one has error
        return;
      }
    } else {
      const localPath = await tryGetLocallyAvailableFile(services, ongoingDownload, localMediaState.isAvailableLocally ? localMediaState.localPath : undefined);
      if (localPath) {
        ongoingDownload.reporters?.forEach((r) => r.onSuccess());
        // try to import from disk immediately, in case this is a single, non-batch import.
        services.ongoingDownloadOps.deleteFromOngoingDownloads(odId);
        await importIntoNle(services, config, ongoingDownload, localPath);

        if (ongoingDownload.artifactReference.type === 'TsSegmentRange' && ongoingDownload.artifactReference.isGrowing && isBuiltinOngoingDownload(ongoingDownload)) {
          services.logger.info(`Immediate import done. We need to continue downloading the growing item ${ongoingDownload.title} (${ongoingDownload.itemId})`);
          refreshClipsAndContinueGrowingFileDownload(services, config, ongoingDownload);
        }

        return;
      }
    }

    // so, we need to start download.

    // Check if we should do local download or SM request
    if (config.isStandaloneMode) {
      ongoingDownload.storageId = PREMIERE_STANDALONE_STORAGE_ID;
      ongoingDownload.id = ongoingDownload.id || createUuid();
      ongoingDownload.yetToBeSentToServer = false;
      ongoingDownload.originalFileName = sourceItem.originalFileName;
    }

    services.notifier?.incrementNumOfFilesToDownload();
    await syncLocalMediaState(
      services,
      {
        item: sourceItem,
        subclip: null,
        downloadIdentity: plan.dlId
      },
    );

    if (plan.delegate == 'Direct') {
      // Download and import without going through the download queue
      invariant(isBuiltinOngoingDownload(ongoingDownload), 'Should have a BuiltinOngoingDownload when downloading media with Direct delegate strategy');
      try {
        await finishDownloadAndImport(services, config, ongoingDownload);
      } catch (err) {
        ongoingDownload.reporters?.forEach((r) => r.onReject(err));
        services.logger.error(`finishDownloadAndImport ${exceptionToString(err)}`);
      }
      return;
    } else {
      /*
        Only desired when not integrating with the UI explicitly. We need to
        migrate away from the download queues and the ongoing collection, since
        they tightly couple the UI reporting with the application logic.
      */
      // This first commit helps to reflect the progress in the UI immediately. Should update if already added.
      services.ongoingDownloadOps.addOrUpdateOngoingDownload(ongoingDownload);
    }

    invariant(storageId, 'Expected storage id to be defined when importing through storage manager!');
    const transferRequestResponse = await sendItemToStorage(
      storageId,
      sourceItem.id,
      artefact,
      ongoingDownload.artifactReference,
    );

    if (transferRequestResponse && transferRequestResponse.id) {
      ongoingDownload.id = transferRequestResponse.id;
      ongoingDownload.yetToBeSentToServer = false;
      ongoingDownload.originalFileName = transferRequestResponse.originalFileName;
      services.ongoingDownloadOps.updateOngoingDownload(ongoingDownload);
      await completeExternalSmDownload(services, config, ongoingDownload, odId, storageId);
    }

  } catch(err) {
    services.logger.error(`Error while creating a transferRequest: ${exceptionToString(err)}`);
    showToast(`Error while importing clip: ${err}`, TOAST.ERROR);
    if (ongoingDownload) {
      await cancelOngoingDownload(services, ongoingDownload, config.skippedFileExtensions);
    }
  }
}

export async function completeExternalSmDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    isStandaloneMode: boolean,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
    shouldUsePremierePreferences: boolean,
    isProxyMode: boolean,
    skippedFileExtensions: string[],
  },
  ongoingDownload: ConstructedOngoingDownload,
  odId: string,
  storageId: string,
): Promise<void> {
  const reporters = ongoingDownload.reporters ?? [];

  let localPath: string | null = null;
  try {
    const monitor = getExternalSmTransferMonitor(storageId, services.applicationContext, services.logger, DEFAULT_DATE_PROVIDER);

    invariant(ongoingDownload.downloadDestination !== undefined, 'downloadDestination must be set before calling completeExternalSmDownload');
    const candidatePaths = getPossibleDestinationPaths(ongoingDownload.downloadDestination);

    invariant(ongoingDownload.id !== undefined, 'Transfer ID must be set before calling completeExternalSmDownload');
    const transferId = ongoingDownload.id;

    localPath = await monitor.waitForLocalFile(transferId, candidatePaths, reporters);

    // Must be synchronous — deleteFromOngoingDownloads runs before the next
    // setInterval macrotask, preventing processTransfers from double-handling.
    services.ongoingDownloadOps.deleteFromOngoingDownloads(odId);

    invariant(localPath !== null, 'localPath must be set when waitForLocalFile resolved');
    await importIntoNle(services, config, ongoingDownload, localPath);
    reporters.forEach((r) => r.onSuccess());
    showTransferCompleteToast(services.applicationContext, ongoingDownload, localPath);
  } catch (err) {
    services.logger.error(`completeExternalSmDownload failed: ${exceptionToString(err)}`);
    reporters.forEach((r) => r.onReject(err));
    throw err;
  } finally {
    if (isInPremiere(services.applicationContext)) {
      await refreshPremiereClipInfos(services, config.skippedFileExtensions);
    }
  }
}

export async function refreshClipsAndContinueGrowingFileDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
    skippedFileExtensions: string[],
  },
  ongoingDownload: BuiltinOngoingDownload,
) {
  // Make sure we wait for refreshing the clipInfos, because we will be unable to update the clip
  // below if the periodic refresh has not happened yet after the initial part was imported.
  await refreshPremiereClipInfos({
    applicationContext: services.applicationContext,
    notificationOps: services.notificationOps,
    logger: services.logger,
    ongoingDownloadOps: services.ongoingDownloadOps,
    localStateOps: services.localStateOps,
  }, config.skippedFileExtensions);

  // Do it without awaiting since this can be a continued process and we do not want to
  // block things (e.g. cleanup of notification toast) that need to be done after this
  // from the caller of finishDownloadAndImport.
  continueGrowingFileDownload(services, config, ongoingDownload);
}

const batchSizeForContinuedDownload = 5;
export async function continueGrowingFileDownload(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
  },
  ongoingDownload: BuiltinOngoingDownload,
) {
  let growingFileSession: GrowingFileSession | undefined;
  let nextFilePath: string | undefined;
  try {
    // Continue to download
    invariant(isInElectron(services.applicationContext), 'Continued download is only supported in DaVinci Resolve');
    const artRef = ongoingDownload.artifactReference;
    invariant(artRef.type === 'TsSegmentRange', 'TsSegmentRange expected for continued download');
    const growingFilePath = getBuiltinDownloadDestinationPath(ongoingDownload.downloadDestination);
    growingFileSession = services.applicationContext.growingFileSessions[growingFilePath];

    if (growingFileSession) {
      // We have come here before for this same item with the same starting segment,
      // and it did not finish properly yet.
      if (growingFileSession.downloadActive) {
        // Download is already active. We should not start another one.
        services.logger.info(`Download already going on for ${growingFilePath}`);
        return;
      }

      // Download may have been inactive due to losing connection with DVR. Resume.
      growingFileSession.downloadActive = true;
      if (growingFileSession.lastSegment !== null) {
        // Resume from last segment that we successfully appended.
        artRef.segmentTimes.last = growingFileSession.lastSegment;
      }
    } else {
      // This is the first time. Initialize the records to keep track.
      growingFileSession = services.applicationContext.growingFileSessions[growingFilePath] = {
        downloadActive: true,
        lastSegment: null,
      };
    }

    if (!await isFileAvailableLocally(services.applicationContext.nodeApi, services.logger, growingFilePath)) {
      throw new Error(`File does not exist: ${growingFilePath}`);
    }

    const artefact = ongoingDownload.artefact!;
    let item: LoadedItem | undefined;
    let moreSegmentsToDownload = false;
    artRef.isGrowing = undefined;
    const originalArtRef = cloneDeep(artRef);
    invariant(originalArtRef.type === 'TsSegmentRange', 'TsSegmentRange expected in originalArtRef for continued download');

    const downloader = services.applicationContext.downloader;
    const backendOps = new DefaultBackendOps(services.applicationContext.backendClient);
    const abortController = new AbortController();
    const abortSignal = abortController.signal;
    const sep = services.applicationContext.pathSep;

    do {
      item = await getItem(ongoingDownload.itemId!);
      const segmentCount = item.media?.segmentCount ?? 0;
      moreSegmentsToDownload = segmentCount > 0 ? artRef.segmentTimes.last < segmentCount - 1 : false;

      if (!moreSegmentsToDownload) {
        // Maybe it is still streaming, but was too slow to grow between our polls. Just wait and retry.
        services.logger.info(`Waiting for item to grow: ${ongoingDownload.title} (${ongoingDownload.itemId})`);
        await sleep(3000, neverCancel);
        continue;
      }

      // We have new content. Set up to download the new segments since last.
      invariant(artRef.type === 'TsSegmentRange', 'TsSegmentRange expected for continued download');
      artRef.segmentTimes.first = artRef.segmentTimes.last + 1;
      artRef.segmentTimes.last = segmentCount - 1;

      // If we download all remaining segments at once, it can take longer to make the local disk file
      // grow. If it is more than 2 minutes, it can in turn make DVR think that it has stopped growing
      // and DVR may no longer watch it for growth and update the duration. For this reason, we are
      // limiting the number of segments to download at a time in each batch.
      if (artRef.segmentTimes.last - artRef.segmentTimes.first + 1 > batchSizeForContinuedDownload) {
        artRef.segmentTimes.last = artRef.segmentTimes.first + batchSizeForContinuedDownload - 1;
      }

      // Download next part
      nextFilePath = `${config.downloadPath}${sep}${item.id}-${artefact}-tsr${artRef.segmentTimes.first},${artRef.segmentTimes.last}.ts.tmp`;

      await downloadItemArtifact(
        {
          downloader,
          backendOps,
        },
        artRef,
        artefact,
        nextFilePath,
        abortSignal,
        () => {},
      );

      // Need to do two things:
      // 1. Update the artifact metadata on the DVR clip, so that it reflects the new segmentTimes.last.
      // 2. Actually append the new data to the growing file.
      // And we should do it in that order, because otherwise if updating the artifact metadata fails
      // for some reason after data is already appended, and the user uses the extended media from the
      // clip in a timeline, we may end up downloading a shorter media during central conform.

      // 1. Need to update the imported clips to reflect new last segment in their artifact metadata.
      const cepb = services.applicationContext.cepBridgeClient;

      // Update originalArtRef BEFORE the clip update below in order to make
      // sure that the last segment we downloaded above is reflected on the clip.
      originalArtRef.segmentTimes.last = artRef.segmentTimes.last;
      let updatedAtLeastOneClip = false;
      try {
        await services.applicationContext.sequenceUploadLock.waitForLock();
        for (const clipInfo of clipInfosArray.value) {
          if (clipInfo.mediaPath !== growingFilePath && clipInfo.proxyPath !== growingFilePath) {
            continue;
          }
          await cepb.call('updateProjectItem', clipInfo.itemRef, {
            meta: {
              [MIMIR_ARTIFACT_KEY]: JSON.stringify(originalArtRef),
            },
            isGrowing: true,
          });
          updatedAtLeastOneClip = true;
        }
      } finally {
        services.applicationContext.sequenceUploadLock.releaseLock();
      }

      if (!updatedAtLeastOneClip) {
        throw new Error('Growing clip does not exist anymore in the project');
      }

      // 2. Append to the growing file
      const nextFileData = await readFileAsync(services.applicationContext.nodeApi, nextFilePath);
      await appendFileAsync(services.applicationContext.nodeApi, growingFilePath, nextFileData);

      // Update session accounting AFTER the two steps above in order to make
      // sure that we re-start the session from the exact last segment that
      // was updated on the clip and appended to the disk file.
      growingFileSession.lastSegment = originalArtRef.segmentTimes.last;

      // Delete the newly downloaded segment
      await deleteFileAsync(services.applicationContext.nodeApi, nextFilePath);

      // We need to continue polling
    } while (isCurrentlyLive(item) || moreSegmentsToDownload);

    services.logger.info(`Stopped continued download for item ${ongoingDownload.title} (${ongoingDownload.itemId}) since it seems no longer growing`);
    delete services.applicationContext.growingFileSessions[growingFilePath];
  } catch (err) {
    if (growingFileSession) {
      growingFileSession.downloadActive = false;
    }

    logErrorAndShowToast(
      `Error during continued download of growing item ${ongoingDownload.title} (${ongoingDownload.itemId})`,
      err,
    );
  } finally {
    if (nextFilePath) {
      await deleteFileAsync(services.applicationContext.nodeApi, nextFilePath);
    }
  }

}

export async function finishDownloadAndImport(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
  },
  config: {
    downloadPath: string,
    userDataSystemPath: string,
    downloadToSubfolderPaths: boolean,
    shouldUsePremierePreferences: boolean,
    isProxyMode: boolean,
    skippedFileExtensions: string[],
  },
  ongoingDownload: BuiltinOngoingDownload,
) {
  try {
    await finishOngoingDownload(services, config, ongoingDownload);
  } catch (err: any) {
    logErrorAndShowToast(
      `Failed to download item ${ongoingDownload.title} (${ongoingDownload.itemId})`,
      err,
    );
    ongoingDownload.cancelTokenSource.cancel(err);
    return;
  }
  const localPath = getBuiltinDownloadDestinationPath(ongoingDownload.downloadDestination);
  await importIntoNle(services, config, ongoingDownload, localPath);

  for (const reporter of ongoingDownload.reporters ?? []) {
    reporter.onSuccess();
  }
  const applicationContext = services.applicationContext;
  if (isInAdobeApp(applicationContext)) {
    await refreshPremiereClipInfos({
      applicationContext,
      notificationOps: services.notificationOps,
      logger: services.logger,
      ongoingDownloadOps: services.ongoingDownloadOps,
      localStateOps: services.localStateOps,
    }, config.skippedFileExtensions);
  }

  if (ongoingDownload.artifactReference.type === 'TsSegmentRange' && ongoingDownload.artifactReference.isGrowing) {
    services.logger.info(`We need to continue downloading the growing item ${ongoingDownload.title} (${ongoingDownload.itemId})`);
    refreshClipsAndContinueGrowingFileDownload(services, config, ongoingDownload);
  }
}
